/**
 * buildTier1Manifest — converts a UnifiedSceneTreeNode into a Tier-1
 * SceneHierarchyManifest in the geosoft.scene-hierarchy/1 schema.
 *
 * Pure function, no side effects. Null/undefined tree → manifest with 0 nodes.
 *
 * Kind / LoR assignment priority (binding rules from WP-A spec):
 *   1. depth == 0           → "region",  LoR 0
 *   2. depth == 1           → "group",   LoR 1  (slot groups)
 *   3. sceneItemIds.length > 0 → "asset", LoR 3  (DEC-20-01 branch point)
 *   4. geometryRefs[].featureId set → "element", LoR 6
 *   5. else                 → "group",   LoR min(depth+1, 8)
 *
 * Block 1 additions:
 *   - Sets objectId = id on every emitted node.
 *   - Populates aspects.spatial.parentId = parentId (mirrors legacy parentId).
 *   - Populates aspects.functional.parentId:
 *     For Tier-1, functional grouping mirrors spatial (no cross-domain
 *     functional info is available from UnifiedSceneTreeNode). The SHB
 *     fixture carries genuinely different functional parents.
 *   - Passes through designation from node.attributes.designation when present.
 *
 * Domain inference is a pure heuristic on layer-kind metadata;
 * no domain-identity branching per DESIGN_POLICY §5 /
 * OBJECT_IDENTITY_AND_SELECTION_POLICY §7.
 */

import type {
  SceneHierarchyManifest,
  UniversalSceneNode,
  TreeNodeDomain,
  TreeNodeKind,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { UnifiedSceneTreeNode } from "@/app/scenePackage";

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — all pure, no side effects
// ─────────────────────────────────────────────────────────────────────────────

function inferDomain(node: UnifiedSceneTreeNode): TreeNodeDomain {
  const rawKind =
    typeof node.attributes?.layerKind === "string"
      ? node.attributes.layerKind
      : node.kind;
  const lk = rawKind.toLowerCase();

  if (lk === "bim" || lk === "ifc-glb") return "building";
  if (lk === "pointcloud" || lk === "copc" || lk === "potree") return "survey";
  if (lk === "panorama" || lk === "pano") return "smartcity";
  if (lk === "3dtiles" && /city|plateau|tokyo/i.test(node.label))
    return "smartcity";
  return "smartcity";
}

function resolveKindAndLor(
  node: UnifiedSceneTreeNode,
  depth: number,
): { kind: TreeNodeKind; lor: number } {
  if (depth === 0) return { kind: "region", lor: 0 };
  if (depth === 1) return { kind: "group", lor: 1 };
  if (node.sceneItemIds.length > 0) return { kind: "asset", lor: 3 };
  const hasFeatureId = node.geometryRefs.some(
    (ref) => ref.featureId != null && ref.featureId !== "",
  );
  if (hasFeatureId) return { kind: "element", lor: 6 };
  return { kind: "group", lor: Math.min(depth + 1, 8) };
}

function convertNode(
  node: UnifiedSceneTreeNode,
  parentId: string | null,
  depth: number,
  sortKey: number,
): UniversalSceneNode {
  const { kind, lor } = resolveKindAndLor(node, depth);
  const domain = inferDomain(node);
  const layerKind =
    typeof node.attributes?.layerKind === "string"
      ? node.attributes.layerKind
      : node.kind;

  // Extract designation from source attributes if present (pass-through).
  const rawDesig = node.attributes?.designation as Record<string, string> | undefined;
  const designation =
    rawDesig &&
    (rawDesig.functional || rawDesig.product || rawDesig.location)
      ? {
          functional: rawDesig.functional,
          product: rawDesig.product,
          location: rawDesig.location,
        }
      : undefined;

  return {
    objectId: node.id,
    id: node.id,
    parentId,
    lor,
    label: node.label,
    kind,
    layerId: node.sceneItemIds[0] ?? null,
    featureId: node.geometryRefs[0]?.featureId ?? null,
    domain,
    standardRef: {
      standard: "geosoft.unified/1",
      classUri: `urn:geosoft:kind:${node.kind}`,
      nativeId: node.id,
    },
    metadata: {
      readiness: node.readiness,
      intelligenceLevel: node.intelligenceLevel ?? null,
      candidateFactCount: node.candidateFacts?.length ?? 0,
      sceneItemIds: [...node.sceneItemIds],
      layerKind,
    },
    hasChildren: node.children.length > 0,
    sortKey,
    // Aspects: spatial mirrors legacy parentId; functional mirrors spatial
    // for Tier-1 (no cross-domain functional grouping available from
    // UnifiedSceneTreeNode). The SHB fixture provides genuine functional parents.
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [],
    ...(designation ? { designation } : {}),
  };
}

function flattenDepthFirst(
  node: UnifiedSceneTreeNode,
  parentId: string | null,
  depth: number,
  siblingIndex: number,
  out: UniversalSceneNode[],
): void {
  out.push(convertNode(node, parentId, depth, siblingIndex));
  for (let i = 0; i < node.children.length; i++) {
    flattenDepthFirst(node.children[i]!, node.id, depth + 1, i, out);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function buildTier1ManifestFromUnifiedTree(
  tree: UnifiedSceneTreeNode | null | undefined,
  gtwinId: string,
): SceneHierarchyManifest {
  const nodes: UniversalSceneNode[] = [];

  if (tree) {
    flattenDepthFirst(tree, null, 0, 0, nodes);
  }

  const nodeCountByLoR: Record<number, number> = {};
  for (const node of nodes) {
    nodeCountByLoR[node.lor] = (nodeCountByLoR[node.lor] ?? 0) + 1;
  }

  const firstAsset = nodes.find((n) => n.kind === "asset");
  const manifestDomain: TreeNodeDomain = firstAsset?.domain ?? "smartcity";
  const rootLayerId = nodes[0]?.layerId ?? "";

  return {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId,
    rootLayerId,
    generatedAt: new Date().toISOString(),
    domain: manifestDomain,
    standardName: "geosoft.unified",
    standardVersion: "1",
    totalNodes: nodes.length,
    nodeCountByLoR,
    nodes,
    paginated: false,
    dataSources: {},
  };
}
